<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useProductStore } from '@/stores/productsStore';
import { useToast } from 'vue-toastification';
import { isOnline } from '@/services/healthCheck';
import { useQzStore } from '@/stores/qzStore';
import axios from 'axios';
import { useErrorLogger } from '@/composables/useErrorLogger';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const productsStore = useProductStore();
const toast = useToast();
const qzStore = useQzStore();
const { logError } = useErrorLogger();

const posProfile = ref('');
const cashierName = ref('');
// const openingFloat = ref(100);
const closingDate = ref('');
const autoRoundToFive = ref(true);
const manualRounding = ref(0);
const floatToLeave = ref(0);
const floatToLeaveTouched = ref(false);
const isSubmitting = ref(false);

const storeId = localStorage.getItem('branchId');

const billBlueprint = [
  { id: 'bill-100', label: '$100', value: 100 },
  { id: 'bill-50', label: '$50', value: 50 },
  { id: 'bill-20', label: '$20', value: 20 },
  { id: 'bill-10', label: '$10', value: 10 },
  { id: 'bill-5', label: '$5', value: 5 }
];

const coinBlueprint = [
  { id: 'coin-2', label: '$2', value: 2 },
  { id: 'coin-1', label: '$1', value: 1 },
  { id: 'coin-25', label: '$0.25', value: 0.25 },
  { id: 'coin-10', label: '$0.10', value: 0.1 },
  { id: 'coin-5', label: '$0.05', value: 0.05 },
  { id: 'coin-1c', label: '$0.01', value: 0.01 },
];

const bills = ref(billBlueprint.map((item) => ({ ...item, quantity: 0 })));
const coins = ref(coinBlueprint.map((item) => ({ ...item, quantity: 0 })));

const otherPayments = ref({
  debit: 0,
  visa: 0,
  masterCard: 0,
  amex: 0,
  interac: 0,
});

const otherPaymentFields = [
  { key: 'debit', label: 'Debit' },
  { key: 'visa', label: 'Visa' },
  { key: 'masterCard', label: 'Master Card' },
  { key: 'amex', label: 'Amex' },
  { key: 'interac', label: 'Interac e-Transfer' },
];

// Get Opening Float Api Call will be here
// const getOpeningFloat = computed(() => {
  
// });

const storeCredits = ref({
  issued: 0,
  redeemed: 0,
  balance: 0,
  notes: ''
});

const currencyFormatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD'
});

function toNumber(value) {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

const billTotal = computed(() =>
  bills.value.reduce((sum, item) => sum + item.value * (toNumber(item.quantity)), 0)
);

const coinTotal = computed(() =>
  coins.value.reduce((sum, item) => sum + item.value * (toNumber(item.quantity)), 0)
);

const totalCashIncludingFloat = computed(() => parseFloat((billTotal.value + coinTotal.value).toFixed(2)));

const autoRoundingValue = computed(() => {
  // No cash counted yet → no rounding
  if (totalCashIncludingFloat.value === 0) return 0;
  const cashDropWithoutRounding = totalCashIncludingFloat.value - toNumber(authStore.openingFloat);
  const remainder = cashDropWithoutRounding % 5;

  if (remainder === 0) return 0;
  return parseFloat((-remainder).toFixed(2));
});


const roundingValue = computed(() =>
  autoRoundToFive.value ? autoRoundingValue.value : parseFloat(toNumber(manualRounding.value).toFixed(2))
);

const floatLeftForNextDay = computed(() => {
  const base = authStore.openingFloat + Math.abs(roundingValue.value);
  return parseFloat(base.toFixed(2));
});

const totalCashDrop = computed(() => {
  const cashDropWithoutRounding = totalCashIncludingFloat.value - toNumber(authStore.openingFloat);
  const drop = cashDropWithoutRounding + roundingValue.value;
  return drop > 0 ? parseFloat(drop.toFixed(2)) : 0;
});

function formatCurrency(amount) {
  return currencyFormatter.format(amount || 0);
}

function handleQuantityInput(item) {
  if (item.quantity === '' || item.quantity === null || Number.isNaN(item.quantity)) {
    item.quantity = 0;
    return;
  }

  if (item.quantity < 0) {
    item.quantity = 0;
  }
}

function selectQty(event) {
  event.target.select();
}

function lineTotal(item) {
  return item.value * (toNumber(item.quantity));
}

function clearLine(item) {
  item.quantity = 0;
}

function handleFloatLeaveInput() {
  floatToLeaveTouched.value = true;
}

watch(authStore.openingFloat, (newVal) => {
  if (!floatToLeaveTouched.value) {
    floatToLeave.value = toNumber(newVal);
  }
});

function resetCounts() {
  bills.value.forEach((item) => {
    item.quantity = 0;
  });
  coins.value.forEach((item) => {
    item.quantity = 0;
  });
}

function clearForm() {
  closingDate.value = '';
  posProfile.value = '';
  cashierName.value = '';
  authStore.openingFloat = 0;
  manualRounding.value = 0;
  autoRoundToFive.value = true;
  floatToLeave.value = 0;
  floatToLeaveTouched.value = false;
  resetCounts();
  otherPayments.value = {
    debit: 0,
    visa: 0,
    masterCard: 0,
    amex: 0,
    interac: 0,
    other: 0
  };
  storeCredits.value = {
    issued: 0,
    redeemed: 0,
    balance: 0,
    notes: ''
  };
}

function handleClose() {
  // authStore.toggleAddItemPopup();
  authStore.isClosingBalacePopupVisible = false;
  clearForm();
}

const runtimeTests = computed(() => {
  const scenarios = [
    { title: 'Test 1: exact multiple', cash: 200, floatGoal: 200, autoRound: true, manualRound: 0 },
    { title: 'Test 2: round down', cash: 203.6, floatGoal: 200, autoRound: true, manualRound: 0 },
    { title: 'Test 3: net below zero', cash: 200, floatGoal: 200, autoRound: false, manualRound: 0 }
  ];

  const simulate = ({ cash, floatGoal, autoRound, manualRound }) => {
    const remainder = cash % 5;
    const autoRoundValue = remainder === 0 ? 0 : parseFloat((5 - remainder).toFixed(2));
    const rounding = autoRound ? autoRoundValue : manualRound;
    const floatLeft = floatGoal + rounding;
    const drop = cash - floatLeft;
    return {
      rounding: rounding.toFixed(2),
      drop: (drop > 0 ? drop : 0).toFixed(2)
    };
  };

  return scenarios.map((scenario) => {
    const remainder = scenario.cash % 5;
    const expectedAuto = remainder === 0 ? 0 : parseFloat((5 - remainder).toFixed(2));
    const expectedRounding = scenario.autoRound ? expectedAuto : scenario.manualRound;
    const expectedDrop = Math.max(
      scenario.cash - (scenario.floatGoal + expectedRounding),
      0
    );

    return {
      title: scenario.title,
      expectedRounding: expectedRounding.toFixed(2),
      expectedDrop: expectedDrop.toFixed(2),
      ...simulate(scenario)
    };
  });
});

// handle submit closing balance

async function handleSubmit() {
  if (totalCashIncludingFloat.value <= 0) {
    toast.error('Please enter a valid cash amount before submitting.');
    return;
  }

  isSubmitting.value = true;
  const userEmail = productsStore.users.find(
    (user) => user.FullName === authStore.getCasherName
  )?.Email || "";

  const payload = {
    user: userEmail,
    date: authStore.date,
    time: new Date().toLocaleTimeString('en-CA', { hour12: false }),
    cashFloat: authStore.openingFloat,
    openingEntry: authStore.openingEntryStatus,
    totalCashIncludingFloat: totalCashIncludingFloat.value,
    float: floatLeftForNextDay.value,
    totalCashDrop: totalCashDrop.value,
    closingNotes: storeCredits.value.notes,
    
    cashCoins: [
      ...bills.value.map((item) => ({
        cashAndCoin: item.value,
        qty: toNumber(item.quantity),
        amount: lineTotal(item)
      })),
      ...coins.value.map((item) => ({
        cashAndCoin: item.value,
        qty: toNumber(item.quantity),
        amount: lineTotal(item)
      }))
    ],
    
    // payment methods
    payment: [
      {
        paymentMethodId: 1,
        amount: toNumber(Math.abs(totalCashDrop.value - roundingValue.value)),
        percentage: 0,
        change: 0,
        couponCode: ""
      },
      {
        paymentMethodId: 2,
        amount: toNumber(otherPayments.value.debit),
        percentage: 0,
        change: 0,
        couponCode: ""
      },
      {
        paymentMethodId: 3,
        amount: toNumber(otherPayments.value.visa),
        percentage: 0,
        change: 0,
        couponCode: ""
      },
      {
        paymentMethodId: 4,
        amount: toNumber(otherPayments.value.masterCard),
        percentage: 0,
        change: 0,
        couponCode: ""
      },
      {
        paymentMethodId: 5,
        amount: toNumber(otherPayments.value.amex),
        percentage: 0,
        change: 0,
        couponCode: ""
      },
      {
        paymentMethodId: 6,
        amount: toNumber(otherPayments.value.interac),
        percentage: 0,
        change: 0,
        couponCode: ""
      }
    ]
  };


  try {
    if (!isOnline.value) {
      toast.warning('Closing the shift requires an internet connection.');
      loading.value = false;
      return;
    }

    const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
    if (!token) {
      toast.error('Authorization token not found. Please log in again.');
      loading.value = false;
      return;
    }

    const response = await axios.post('/api/Closing/NewClosing', payload , {
      headers: {
        'store-id': storeId,
        'Authorization': `Bearer ${token}`
      }
    });

    if(response.status === 200) {
      console.log('Closing balance submitted successfully:', response.data);
      
      const amountToPrint = totalCashDrop.value;

      clearForm();
      qzStore.printCashDrop({
        amount: amountToPrint,
        date: authStore.date,
        closerName: authStore.getCasherName,
        posProfile: authStore.getPosProfileName
      });
      authStore.openingStatus = 'closed';
      authStore.isClosingBalacePopupVisible = false;
      toast.success('Closing balance submitted successfully.');
      
    } else {
      console.error('Failed to submit closing balance:', response.error);
      toast.error('Failed.', response.error);
    }

  } catch(error) {
    console.error('Error submitting closing balance:', error);
    logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'ClosingBalancePopup', action: 'submitClosingBalance', branchId: authStore.branchId, user: authStore.currentUser } });
  } finally {
    isSubmitting.value = false;
  }
}

</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div
        v-if="authStore.isClosingBalacePopupVisible"
        class="absolute z-10 w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center items-center px-6"
      >
        <Transition name="modal-inner" class="rounded-2xl">
          <!-- Modal container: fixed size, overflow-hidden and column layout -->
          <div class="fixed top-10 z-50 bg-white rounded-3xl shadow-xl w-10/12 max-h-[90vh] overflow-hidden flex flex-col">
            <!-- HEADER (fixed height, non-scrolling) -->
            <header class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 px-6 pt-6 bg-white flex-shrink-0">
              <div>
                <h1 class="text-3xl font-semibold text-gray-900">End Of Day Closing</h1>
              </div>
            </header>
            <button
                type="button"
                class="absolute right-0 top-4 pi pi-times px-6 py-2 text-gray-600 font-medium"
                @click.stop="!isSubmitting && handleClose()"
                :disabled="isSubmitting"
                :class="{ 'opacity-40 cursor-not-allowed': isSubmitting }"
            ></button>

            <!-- SCROLLABLE CONTENT -->
            <div class="flex-1 overflow-auto p-6 space-y-6">
              <section class="bg-[#f4f5f7] border-gray-100 rounded-3xl px-6 py-6 shadow-sm">
                <!-- <h2 class="text-lg font-semibold text-gray-800 mb-4">Header</h2> -->
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Date</label>
                    <!-- <input v-model="closingDate" type="date" class="form-input" readonly /> -->
                     <span class="bg-white p-2 rounded-xl">{{ orderStore.formatDate(authStore.date) }}</span>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">POS Profile</label>
                    <span class="bg-white p-2 rounded-xl">
                      {{ authStore.getPosProfileName }}
                    </span>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Cashier</label>
                    <span class="bg-white p-2 rounded-xl">
                      {{ authStore.getCasherName }}
                    </span>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Opening Float</label>
                    <span class="bg-white p-2 rounded-xl">
                      {{ authStore.openingFloat }}
                    </span>
                  </div>
                  <!-- <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Auto-Round to $5</label>
                    <label class="flex items-center gap-3">
                      <input v-model="autoRoundToFive" type="checkbox" class="toggle" />
                      <span class="text-sm text-gray-600">Enabled</span>
                    </label>
                  </div> -->
                  <!-- <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Manual Rounding — Only used when Auto-Round is off</label>
                    <input
                      v-model.number="manualRounding"
                      type="number"
                      step="0.01"
                      class="form-input"
                      :disabled="autoRoundToFive"
                    />
                  </div> -->
                </div>
              </section>

              <section class="bg-white border border-purple-100 rounded-3xl px-6 py-6 shadow-sm space-y-6">
                <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 class="text-lg font-semibold text-gray-800">Count Cash</h2>
                  </div>
                  <div class="flex gap-3">
                    <button
                      type="button"
                      class="px-5 py-2 bg-purple-100 text-purple-700 rounded-xl font-medium hover:bg-purple-200 transition-colors"
                      @click.stop="resetCounts"
                    >
                      Reset
                    </button>
                    <!-- <button
                      type="button"
                      class="px-5 py-2 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                      @click.stop="clearForm"
                    >
                      Clear Form
                    </button> -->
                  </div>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" style="margin-top: 0 !important;">
                  <section>
                    <h3 class="text-md font-semibold text-gray-700 mb-3">Bills</h3>
                    <div class="grid grid-cols-1 sm:grid-cols-1 gap-4">
                      <div
                        v-for="bill in bills"
                        :key="bill.id"
                        class="rounded-2xl border-gray-100 bg-[#f4f5f7] p-4"
                      >
                        <div class="flex items-start justify-between mb-3">
                          <p class="text-base font-semibold text-gray-900">{{ bill.label }}</p>
                        </div>
                        <div class="flex items-center justify-between">
                          <input
                            v-model.number="bill.quantity"
                            type="number"
                            min="0"
                            class="w-24 rounded-xl border border-gray-100 bg-white px-3 py-2 text-center text-base font-semibold text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
                            @input="handleQuantityInput(bill)"
                            @focus="selectQty"
                          />
                          <div class="text-right">
                            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Line Total</p>
                            <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(lineTotal(bill)) }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 class="text-md font-semibold text-gray-700 mb-3">Coins</h3>
                    <div class="grid grid-cols-1 sm:grid-cols-1 gap-4">
                      <div
                        v-for="coin in coins"
                        :key="coin.id"
                        class="rounded-2xl border-purple-100 bg-[#f4f5f7] p-4"
                      >
                        <div class="flex items-start justify-between mb-3">
                          <p class="text-base font-semibold text-gray-900">{{ coin.label }}</p>
                        </div>
                        <div class="flex items-center justify-between">
                          <input
                            v-model.number="coin.quantity"
                            type="number"
                            min="0"
                            class="w-24 rounded-xl border border-purple-100 bg-white px-3 py-2 text-center text-base font-semibold text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
                            @input="handleQuantityInput(coin)"
                            @focus="selectQty"
                          />
                          <div class="text-right">
                            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Line Total</p>
                            <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(lineTotal(coin)) }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-2">
                  <div class="rounded-2xl border-gray-100 bg-gray-100 px-6 py-4">
                    <p class="text-sm font-medium text-gray-500">Bills Total</p>
                    <p class="text-2xl font-semibold text-gray-900 mt-1">{{ formatCurrency(billTotal) }}</p>
                  </div>
                  <div class="rounded-2xl border-gray-100 bg-gray-100 px-6 py-4">
                    <p class="text-sm font-medium text-gray-500">Coins Total</p>
                    <p class="text-2xl font-semibold text-gray-900 mt-1">{{ formatCurrency(coinTotal) }}</p>
                  </div>
                  <div class="rounded-2xl border-gray-100 bg-gray-100 px-6 py-4">
                    <p class="text-sm font-medium text-gray-500">Total Cash Including Float</p>
                    <p class="text-2xl font-semibold text-purple-700 mt-1">{{ formatCurrency(totalCashIncludingFloat) }}</p>
                  </div>
                  <!-- <div class="rounded-2xl border-gray-200 bg-gray-100 px-6 py-4">
                    <p class="text-sm font-medium text-gray-500">Float To Leave</p>
                    <input
                      v-model.number="floatToLeave"
                      type="number"
                      min="0"
                      step="0.01"
                      class="mt-2 form-input"
                      @input="handleFloatLeaveInput"
                    />
                  </div> -->
                </div>
              </section>

              <section class="bg-[#f4f5f7] border border-purple-100 rounded-3xl px-6 py-6 shadow-sm space-y-4">
                <h2 class="text-lg font-semibold text-gray-800">Cash Drop</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <!-- <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Rounding</label>
                    <input type="text" :value="formatCurrency(roundingValue)" class="form-input" disabled />
                  </div> -->
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Total Cash Drop</label>
                    <input type="text" :value="formatCurrency(totalCashDrop)" class="form-input" disabled />
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Float Left For Next Day</label>
                    <input type="text" :value="formatCurrency(authStore.openingFloat)" class="form-input" disabled />
                  </div>
                </div>
              </section>

              <section class="bg-[#f4f5f7] rounded-3xl px-6 py-6 shadow-sm space-y-4">
                <h2 class="text-lg font-semibold text-gray-800">Other Modes of Payment</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="flex flex-col gap-2" v-for="field in otherPaymentFields" :key="field.key">
                    <label class="text-sm font-medium text-gray-600">{{ field.label }}</label>
                    <input
                      v-model.number="otherPayments[field.key]"
                      type="number"
                      class="form-input"
                      @focus="selectQty"
                    />
                  </div>
                </div>
              </section>

              <section class="bg-[#f4f5f7] rounded-3xl px-6 py-6 shadow-sm space-y-4">
                <h2 class="text-lg font-semibold text-gray-800">Store Credits &amp; Notes</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Credits Issued</label>
                    <input v-model.number="storeCredits.issued" type="number" min="0" class="form-input" />
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Store Credits Redeemed</label>
                    <input v-model.number="storeCredits.redeemed" type="number" min="0" class="form-input" />
                  </div>
                  <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-gray-600">Credits Balance</label>
                    <input v-model.number="storeCredits.balance" type="number" min="0" class="form-input" />
                  </div>
                </div>
                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-gray-600">Closing Notes</label>
                  <textarea v-model="storeCredits.notes" rows="4" class="form-textarea"></textarea>
                </div>
              </section>

              <!-- <section class="bg-[#f4f5f7] rounded-3xl px-6 py-6 shadow-sm space-y-4">
                <h2 class="text-lg font-semibold text-gray-800">Runtime Tests</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    v-for="test in runtimeTests"
                    :key="test.title"
                    class="border border-purple-100 rounded-2xl bg-white p-4 shadow-sm"
                  >
                    <p class="font-semibold text-gray-800 mb-2">{{ test.title }}</p>
                    <p class="text-sm text-gray-600">expected rounding = {{ test.expectedRounding }}; drop = {{ test.expectedDrop }}</p>
                    <p class="text-sm text-gray-600">actual rounding = {{ test.rounding }}; drop = {{ test.drop }}</p>
                  </div>
                </div>
              </section> -->
            </div>

            <!-- FOOTER (fixed height, non-scrolling) -->
            <footer class="flex justify-end gap-4 p-6 bg-white flex-shrink-0">
              <button
                  type="button"
                  class="relative px-8 py-2 bg-purple-600 text-white rounded-xl font-semibold shadow-sm hover:bg-purple-700 transition-colors"
                  @click.stop="handleSubmit"
                  :disabled="isSubmitting || !isOnline"
                  :class="{ 'opacity-40 cursor-not-allowed': isSubmitting || !isOnline }"
                  :title="!isOnline ? 'Requires an internet connection' : ''"
                >
                  {{ isSubmitting ? "Submitting..." : "Submit" }}
                  <span v-if="!isOnline" class="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-600 animate-pulse" title="Requires an internet connection"></span>
              </button>
            </footer>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

.form-input {
  @apply w-full rounded-xl border border-purple-100 bg-white px-4 py-2 text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100;
}

.form-textarea {
  @apply w-full rounded-xl border border-purple-100 bg-white px-4 py-3 text-gray-900 shadow-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100;
}

.toggle {
  width: 3rem;
  height: 1.5rem;
}
</style>
